import React, { useMemo } from 'react';
import QRCode from 'react-qr-code';
import PropTypes from 'prop-types';

import {
  CryptoFormatter,
  IntlManager,
  NotificationManager,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { BasePopup, Button, HeaderTitle, Icon, Label } from '@atlas/ui';

const withSpecificCopyTextPayments = ['coinspaid', 'coinspaidV2'];

const parseQuery = queryString => {
  const query = {};
  const pairs = queryString.substring(queryString.indexOf('?') + 1).split('&');
  for (const element of pairs) {
    const pair = element.split('=');
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }
  return query;
};

const BitcoinPopup = ({ data, id }) => {
  const { lang } = useSettings();
  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);
  const { redirectUrl } = data;

  const paymentMethodName = redirectUrl.split('?')[0];

  const isSpecificCopyText = React.useMemo(() => {
    if (!paymentMethodName) return false;
    return withSpecificCopyTextPayments.includes(paymentMethodName);
  }, [paymentMethodName]);

  const { address, amount, currencyTo, exchangeRate, tag } = parseQuery(redirectUrl);
  const [isCopied, setIsCopied] = React.useState(false);

  const textParams = React.useMemo(() => ({ currency: currencyTo }), [currencyTo]);

  const buttonLabel = React.useMemo(() => {
    return isCopied ? 'copied' : isSpecificCopyText ? 'copy_address' : 'copy_pix';
  }, [isSpecificCopyText, isCopied]);

  const handleClose = React.useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const copy = React.useCallback(
    e => {
      e.preventDefault();
      const inp = document.createElement('textarea');
      document.body.appendChild(inp);
      inp.value = address;
      inp.select();
      document.execCommand('copy', false);
      document.body.removeChild(inp);
      setIsCopied(true);
    },
    [address],
  );

  const currencyToFormatter = useMemo(() => {
    return new CryptoFormatter(languageCode, currencyTo);
  }, [languageCode, currencyTo]);

  return (
    <BasePopup className="crypto-modal">
      <HeaderTitle className="left border block-big size-xs">
        <Label className="sub-title-500-text" message={currencyTo} />
        <div className="header-title__children">
          <button onClick={handleClose} type="button">
            <Icon name="close" />
          </button>
        </div>
      </HeaderTitle>

      <div className="crypto-modal__content">
        <Label
          className="popup__text"
          message="crypto_deposit_message_currency"
          params={textParams}
        />

        <div className="crypto-modal__inner">
          <div className="crypto-modal-item">
            <div className="crypto-modal-item__name">
              <Label className="crypto-modal-item__name" message="withdrawal_rate" />:
            </div>

            <div className="crypto-modal-item__value">
              {`${currencyToFormatter.format(10 ** 9)} = ${IntlManager.formatMoney(exchangeRate * 100)}`}
            </div>
          </div>

          <div className="crypto-modal-item">
            <div className="crypto-modal-item__name">
              <Label message="amount" />:
            </div>
            <div className="crypto-modal-item__value">
              {currencyToFormatter.format(amount * 10 ** 9)}
            </div>
          </div>

          {tag && tag !== 'null' && (
            <div className="crypto-modal-item">
              <div className="crypto-modal-item__name">
                <Label message="tag" />:
              </div>
              <div className="crypto-modal-item__value">{tag}</div>
            </div>
          )}
        </div>

        <div className="crypto-modal__qr">
          <QRCode size={150} value={address} />
        </div>

        <div className="crypto-modal__code">{address}</div>

        <Button
          className="primary big block"
          disabled={isCopied}
          label={buttonLabel}
          onClick={copy}
        />
      </div>
    </BasePopup>
  );
};

BitcoinPopup.propTypes = {
  data: PropTypes.shape({
    redirectUrl: PropTypes.string,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
export default BitcoinPopup;
